
import React, { createContext, useContext, useState, ReactNode } from 'react';
import { AlertTriangle, X, ShieldAlert } from 'lucide-react';

interface ConfirmationOptions {
  title: string;
  message: string;
  confirmLabel?: string;
  cancelLabel?: string;
  variant?: 'danger' | 'warning' | 'info';
  onConfirm: () => void;
}

interface ConfirmationContextType {
  requestConfirmation: (options: ConfirmationOptions) => void;
}

const ConfirmationContext = createContext<ConfirmationContextType | undefined>(undefined);

export const useConfirmation = () => {
  const context = useContext(ConfirmationContext);
  if (!context) {
    throw new Error('useConfirmation must be used within a ConfirmationProvider');
  }
  return context;
};

export const ConfirmationProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [options, setOptions] = useState<ConfirmationOptions | null>(null);

  const requestConfirmation = (opts: ConfirmationOptions) => {
    setOptions(opts);
    setIsOpen(true);
  };

  const handleConfirm = () => {
    if (options?.onConfirm) options.onConfirm();
    setIsOpen(false);
    setTimeout(() => setOptions(null), 300);
  };

  const handleCancel = () => {
    setIsOpen(false);
    setTimeout(() => setOptions(null), 300);
  };

  return (
    <ConfirmationContext.Provider value={{ requestConfirmation }}>
      {children}
      {isOpen && options && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-6 bg-slate-900/60 backdrop-blur-sm animate-in fade-in duration-200">
          <div className="w-full max-w-sm bg-white dark:bg-slate-900 rounded-[2rem] shadow-2xl p-8 space-y-6 animate-in zoom-in-95 duration-200 border border-slate-100 dark:border-slate-800 relative overflow-hidden">
            <div className={`absolute top-0 left-0 w-full h-1 ${options.variant === 'danger' ? 'bg-rose-500' : options.variant === 'warning' ? 'bg-amber-500' : 'bg-blue-500'}`} />
            
            <div className="flex items-center gap-4">
              <div className={`w-14 h-14 rounded-2xl flex items-center justify-center shrink-0 ${options.variant === 'danger' ? 'bg-rose-50 text-rose-600 dark:bg-rose-900/20 dark:text-rose-400' : options.variant === 'warning' ? 'bg-amber-50 text-amber-600 dark:bg-amber-900/20 dark:text-amber-400' : 'bg-blue-50 text-blue-600 dark:bg-blue-900/20 dark:text-blue-400'}`}>
                {options.variant === 'danger' ? <ShieldAlert size={28} /> : <AlertTriangle size={28} />}
              </div>
              <div>
                <h3 className="text-xl font-black text-slate-900 dark:text-slate-100 tracking-tight leading-none">{options.title}</h3>
                <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mt-1">Confirmation Required</p>
              </div>
            </div>
            
            <p className="text-sm font-medium text-slate-600 dark:text-slate-300 leading-relaxed">
              {options.message}
            </p>

            <div className="flex gap-3 pt-2">
              <button 
                onClick={handleCancel}
                className="flex-1 py-3.5 text-xs font-bold text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-xl transition-colors uppercase tracking-wider"
              >
                {options.cancelLabel || 'Cancel'}
              </button>
              <button 
                onClick={handleConfirm}
                className={`flex-[1.5] py-3.5 text-white text-xs font-black rounded-xl shadow-lg transition-all uppercase tracking-wider active:scale-95 ${
                  options.variant === 'danger' 
                    ? 'bg-rose-600 hover:bg-rose-700 shadow-rose-600/20' 
                    : options.variant === 'warning'
                      ? 'bg-amber-500 hover:bg-amber-600 shadow-amber-500/20 text-white'
                      : 'bg-blue-600 hover:bg-blue-700 shadow-blue-600/20'
                }`}
              >
                {options.confirmLabel || 'Confirm'}
              </button>
            </div>
          </div>
        </div>
      )}
    </ConfirmationContext.Provider>
  );
};
